import { ReactNode } from 'react';
import {
  useCurrentFrame,
  interpolate,
  spring,
  useVideoConfig,
  SpringConfig,
} from 'remotion';

// Components
import { SlotMachineNumber } from '@/components/common/animations/SlotMachineNumber';
import { WaveTextReveal } from '@/components/common/animations/WaveTextReveal';
import { TypingText } from '@/components/common/animations/TypingText';

// Fonts
import { interFontFamily } from '@/styles/fonts';

// Constants
import { ANIMATION_SPRING_CONFIGS, CLAMP_BOTH } from '@/constants';

// Themes
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';

// Utils
import { combineClasses } from '@/utils';
import { getRecentCertificates, calculateCertificateTimings } from '../utils';

// Types
import { AnimationPreset, EmployeeCertification } from '@/types';

const SLIDE_SPRING_CONFIG: Partial<SpringConfig> = {
  damping: 30,
  stiffness: 40,
  mass: 0.7,
};

// One-time entry timing
const ENTRY_DELAY = 8;
const NUMBER_START = 15;
const CERTIFICATES_START = 55;
const COMPLETED_START = 75;

interface NumberSectionProps {
  totalCertificates: number;
  certificates: EmployeeCertification[];
  animationDelay?: number;
}

/**
 * Persistent number block that slides left/right across transitions.
 */
export const NumberSection = ({
  totalCertificates,
  certificates,
  animationDelay = 0,
}: NumberSectionProps): ReactNode => {
  const { colors, typography } = employeeYearInReviewTheme;
  const rawFrame = useCurrentFrame();
  const frame = Math.max(0, rawFrame - animationDelay);
  const { fps } = useVideoConfig();
  const limited = getRecentCertificates(certificates);
  const timings = calculateCertificateTimings(limited);

  // One-time slide-in from left
  const entryProgress = spring({
    frame: frame - ENTRY_DELAY,
    fps,
    config: ANIMATION_SPRING_CONFIGS[AnimationPreset.SNAPPY],
  });

  const entryOpacity = interpolate(entryProgress, [0, 0.3], [0, 1], CLAMP_BOTH);

  const entryTranslateX = interpolate(
    entryProgress,
    [0, 1],
    [-100, 0],
    CLAMP_BOTH,
  );

  // Slide position (0 = left, 1 = right) driven by transition springs
  let slidePosition = 0;

  for (let i = 0; i < timings.length - 1; i++) {
    const timing = timings[i];
    if (timing && frame >= timing.transitionStartFrame) {
      const rawSpringProgress = spring({
        frame: frame - timing.transitionStartFrame,
        fps,
        config: SLIDE_SPRING_CONFIG,
      });

      // Snap only near the ends to avoid late-frame jitter
      const springProgress =
        rawSpringProgress > 0.99
          ? 1
          : rawSpringProgress < 0.01
            ? 0
            : rawSpringProgress;

      // Even transitions slide right, odd slide left
      slidePosition = i % 2 === 0 ? springProgress : 1 - springProgress;
    }
  }

  // Final snap only at extremes to keep deceleration smooth
  const finalSlidePosition =
    slidePosition > 0.99 ? 1 : slidePosition < 0.01 ? 0 : slidePosition;

  // Map position to left% and translateX% for width-agnostic sliding
  const leftPercent = interpolate(
    finalSlidePosition,
    [0, 1],
    [0, 100],
    CLAMP_BOTH,
  );
  const translateXPercent = interpolate(
    finalSlidePosition,
    [0, 1],
    [0, -100],
    CLAMP_BOTH,
  );

  // Align text based on midpoint
  const isRightAligned = finalSlidePosition > 0.5;

  return (
    <div
      className="absolute inset-x-0 top-0 py-20 px-72"
      style={{ opacity: entryOpacity }}
    >
      <div className="relative w-full">
        <div
          className={combineClasses(
            colors.textPrimary,
            'relative w-fit',
            isRightAligned ? 'text-right' : 'text-left',
          )}
          style={{
            left: `${leftPercent}%`,
            transform: `translateX(${translateXPercent}%) translateX(${entryTranslateX}px)`,
          }}
        >
          <p
            className={combineClasses(
              'font-bold h-fit -my-24 text-[300px]',
              isRightAligned ? '-mr-4' : '-mx-4',
            )}
            style={{
              fontFamily: interFontFamily,
            }}
          >
            <SlotMachineNumber
              value={totalCertificates}
              startFrame={NUMBER_START}
              spinDuration={45}
            />
          </p>
          <p className={combineClasses(typography.xs)}>
            <WaveTextReveal
              text={`certificate${totalCertificates === 1 ? '' : 's'}`}
              startFrame={CERTIFICATES_START}
              staggerFrames={3}
              waveAmplitude={12}
            />
          </p>
          <div
            className={combineClasses(
              typography.lg,
              'font-extrabold',
              colors.textPrimary,
            )}
            style={{
              fontFamily: interFontFamily,
            }}
          >
            <TypingText
              text="completed"
              startFrame={COMPLETED_START}
              charsPerFrame={1}
              showCursor={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
